// Unified analytics dispatcher for Google Analytics 4, Google Ads, and Meta Pixel.
//
// Usage:
//   import { trackEvent, trackSignup, trackEnquiry, trackPageView } from "@/lib/analytics";
//
// All events automatically include any persisted UTM data (see lib/utm.ts) so
// you can attribute conversions to the originating Google/Meta campaign in
// each vendor's reporting UI.

import { TRACKING } from "@/config/tracking";
import { getSignupUtm } from "@/lib/utm";

export const CONSENT_KEY = "eal-advertising-consent-v1";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & { callMethod?: unknown; queue?: unknown[] };
    _fbq?: unknown;
    __eafl_tracking_loaded?: boolean;
  }
}

function hasConsent(): boolean {
  // Private address forms and demo visits are never tracked, so demo visitors never enter reporting or ad audiences.
  if (typeof window === "undefined") return false;
  if (/^\/(address-request|demo|admin|auth|reset-password|supplier|venue|couple)(\/|$)/.test(window.location.pathname)) return false;
  try {
    if (!["everafterlist.com", "www.everafterlist.com"].includes(window.location.hostname)) return false;
    return localStorage.getItem(CONSENT_KEY) === "accepted";
  } catch {
    return false;
  }
}

/** Inject GA4 + Google Ads + Meta Pixel script tags exactly once. */
export function loadTrackingScripts(): void {
  if (typeof window === "undefined") return;
  if (window.__eafl_tracking_loaded) return;
  if (!hasConsent()) return;

  const { ga4Id, metaPixelId, googleAdsId } = TRACKING;

  // --- gtag.js (GA4 + Google Ads share the same gtag runtime) -----------
  if (ga4Id || googleAdsId) {
    const primaryId = ga4Id || googleAdsId;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
    document.head.appendChild(s);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
    window.gtag("consent", "default", { ad_storage: "denied", analytics_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
    window.gtag("consent", "update", { ad_storage: "granted", analytics_storage: "denied", ad_user_data: "granted", ad_personalization: "denied" });
    window.gtag("set", { allow_ad_personalization_signals: false, page_location: window.location.origin + window.location.pathname, page_referrer: "" });
    window.gtag("js", new Date());
    if (ga4Id) window.gtag("config", ga4Id, { send_page_view: false });
    if (googleAdsId) window.gtag("config", googleAdsId);
  }

  // --- Meta Pixel ------------------------------------------------------
  if (metaPixelId) {
    /* eslint-disable */
    // Standard FB pixel bootstrap, lightly adapted to TS.
    (function (f: any, b, e, v, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    window.fbq!("init", metaPixelId);
  }

  window.__eafl_tracking_loaded = true;
}

/** Send a page_view to GA4 and a PageView to Meta. Call on route change. */
export function trackPageView(path: string): void {
  if (!hasConsent()) return;
  loadTrackingScripts();
  const { ga4Id } = TRACKING;
  if (window.gtag && ga4Id) {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      ...getSignupUtm(),
    });
  }
  if (window.fbq) window.fbq("track", "PageView");
}

interface EventParams {
  /** Vendor-agnostic event name, e.g. "signup_supplier", "enquiry_submitted". */
  name: string;
  /** Optional monetary value (used for Meta + GA4 conversion value). */
  value?: number;
  currency?: string;
  /** Any extra properties to forward to vendors. */
  params?: Record<string, unknown>;
  /** Meta standard event to fire alongside the custom event. */
  metaEvent?: "Lead" | "CompleteRegistration" | "Subscribe" | "Contact" | "ViewContent";
  /** Google Ads conversion label to fire (paired with googleAdsId). */
  googleAdsLabel?: string;
  transactionId?: string;
}

/** Fire a unified event across GA4, Google Ads, and Meta. */
export function trackEvent({
  name,
  value,
  currency = "GBP",
  params,
  metaEvent,
  googleAdsLabel,
  transactionId,
}: EventParams): void {
  if (!hasConsent()) return;
  loadTrackingScripts();

  const utm = getSignupUtm();
  const payload = { ...utm, ...params, ...(value !== undefined ? { value, currency } : {}) };

  // GA4
  if (window.gtag && TRACKING.ga4Id) {
    window.gtag("event", name, payload);
  }
  // Google Ads conversion
  if (window.gtag && TRACKING.googleAdsId && googleAdsLabel) {
    window.gtag("event", "conversion", {
      send_to: `${TRACKING.googleAdsId}/${googleAdsLabel}`,
      ...(transactionId ? { transaction_id: transactionId } : {}),
      ...(value !== undefined ? { value, currency } : {}),
    });
  }
  // Meta Pixel
  if (window.fbq) {
    if (metaEvent) {
      window.fbq("track", metaEvent, payload);
    }
    window.fbq("trackCustom", name, payload);
  }
}

/** Signup conversion. audience = "supplier" | "venue". */
const recordedSignups = new Set<string>();
export function trackSignup(audience: "supplier" | "venue", extra?: Record<string, unknown>, signupId?: string) {
  if (!hasConsent()) return;
  if (signupId && recordedSignups.has(signupId)) return;
  try {
    trackEvent({
    value: 1,
    transactionId: crypto.randomUUID(),
    name: `signup_${audience}`,
    metaEvent: "CompleteRegistration",
    googleAdsLabel: TRACKING.googleAdsSignupLabel || undefined,
    params: { audience, ...extra },
  });
    if (signupId) recordedSignups.add(signupId);
  } catch { /* Tracking must not prevent successful account creation. */ }
}

/** Enquiry / lead conversion (couple → supplier). */
export function trackEnquiry(extra?: Record<string, unknown>) {
  trackEvent({
    name: "enquiry_submitted",
    metaEvent: "Lead",
    googleAdsLabel: TRACKING.googleAdsEnquiryLabel || undefined,
    params: extra,
  });
}
